import { CheckCircleFillIcon, LockIcon, MarkGithubIcon, type OcticonProps, RepoIcon, XCircleFillIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { type ComponentType, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAction, useConnectionRepos, useMe, useRepos } from "../api/hooks";
import type { Connection, ImportProvider, ProviderRepo } from "../api/types";
import { Badge } from "../components/ui/badge";
import { AdminsOnly, ErrorMessage, Loading, Page, PageHead } from "../components/common";
import { forge } from "../components/forge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Field, FieldRow, FieldRows, FieldStack, FormStep, FormSteps } from "../components/ui/field";
import { cn } from "@/lib/utils";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { connectHref } from "./Login";

/** Mirrors the server's NameRe (repos/repository.go). */
const REPO_RE = /^[a-z0-9][a-z0-9._-]{0,62}$/;

const DESC_MAX = 500;

export function NewRepo() {
  const me = useMe();
  if (me.isLoading) {
    return (
      <Page narrow>
        <Loading />
      </Page>
    );
  }
  // The server also accepts the bootstrap admin token (ResolveOwner), which is an actor without a user.
  if (!me.data?.user && me.data?.actor.kind !== "admin") {
    return (
      <AdminsOnly>
        Creating a repository needs you to sign in as a person.
      </AdminsOnly>
    );
  }
  return <CreateRepo />;
}

type Source = "empty" | ImportProvider;

const SOURCES: Array<{ id: Source; label: string; hint: string; Icon: ComponentType<OcticonProps>; placeholder?: string }> = [
  { id: "github", label: "Import from GitHub", hint: "Copy a repository and its history.", Icon: MarkGithubIcon, placeholder: "https://github.com/acme/shop" },
  { id: "empty", label: "Empty repository", hint: "Start fresh; agents build it up.", Icon: RepoIcon },
];

/** Loose shape check; the server does the real validation (ghrepo.Parse). */
const URL_RE = /^(https?:\/\/|ssh:\/\/|[A-Za-z0-9._-]+@[A-Za-z0-9.-]+:)\S+\/\S+$/;

/** Mirrors ghrepo.Ref.RepoName: the last path segment as a valid repo name. */
function repoNameFromUrl(url: string): string {
  const last = url.trim().replace(/\/+$/, "").replace(/\.git$/, "").split(/[/:]/).pop() ?? "";
  return last.toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^[-._]+|[-._]+$/g, "").slice(0, 63);
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Search the repositories a connection can import; picking one fills the form. */
function RepoPicker({ connection, providerName, onPick }: { connection: Connection; providerName: string; onPick: (r: ProviderRepo) => void }) {
  const [q, setQ] = useState("");
  const repos = useConnectionRepos(connection.id, useDebounced(q.trim(), 250));
  return (
    <div className="mb-4">
      <Field label={`Your ${providerName} repositories`} hint={`Listed with ${connection.login}'s connection on ${connection.host}; Bee uses it to clone, push and pull.`}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" />
      </Field>
      <div className="mt-2 max-h-64 overflow-y-auto rounded-md border">
        {repos.error ? (
          <div className="p-3">
            <ErrorMessage error={repos.error} />
          </div>
        ) : repos.isLoading ? (
          <div className="p-3 text-muted-foreground">Loading…</div>
        ) : repos.data?.length ? (
          repos.data.map((r) => (
            <button
              key={r.url}
              type="button"
              className="flex w-full items-center gap-2 border-b px-3 py-2 text-left last:border-b-0 hover:bg-transparent-hover"
              onClick={() => onPick(r)}
            >
              {r.private ? <LockIcon className="text-muted-foreground" /> : <RepoIcon className="text-muted-foreground" />}
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{r.path}</span>
                {r.description && <span className="block truncate text-xs text-muted-foreground">{r.description}</span>}
              </span>
              {!r.push && <Badge title="You cannot push: a remote needs write access">read-only</Badge>}
            </button>
          ))
        ) : (
          <div className="p-3 text-muted-foreground">No repositories match.</div>
        )}
      </div>
    </div>
  );
}

/** How Bee reaches the source: the user's OAuth connection. */
function SourceAccess({ source, url, onPick, children }: { source: ImportProvider; url: string; onPick: (r: ProviderRepo) => void; children: ReactNode }) {
  const me = useMe();
  const f = forge(source);
  const [open, setOpen] = useState(false);
  const connection = me.data?.connections.find((c) => c.provider === source && c.status === "ok") ?? null;
  const provider = me.data?.providers.find((p) => p.id === source);
  const canConnect = !!me.data?.user && !!provider;
  return (
    <div className="mb-4">
      {connection ? (
        <>
          <p className="mb-3 flex items-center gap-1.5 text-sm text-success">
            <CheckCircleFillIcon size={14} /> Connected to {connection.host} as {connection.login}
          </p>
          <RepoPicker key={connection.id} connection={connection} providerName={f.name} onPick={onPick} />
          <div className="mt-4">{children}</div>
        </>
      ) : (
        <>
        {children}
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Give bee access to {f.name}</div>
            <p className="text-xs text-muted-foreground">Only needed for private repositories and pushing back.</p>
          </div>
          <Button type="button" onClick={() => setOpen(true)}>
            Give access
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="top-[16vh] w-[min(480px,calc(100vw-32px))] p-5">
              <DialogTitle className="text-base font-semibold">Give bee access to {f.name}</DialogTitle>
              <div className="mt-4 grid gap-3">
                <div className="rounded-md border p-3">
                  <div className="font-medium">Connect {f.name}</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {canConnect ? "Sign in with your account. Bee can then list your repositories." : me.data?.user ? `Sign-in with ${f.name} is not set up on this server.` : `Sign in to Bee with ${f.name} first.`}
                  </p>
                  {canConnect ? (
                    <Button asChild variant="primary" className="mt-2">
                      {/* A full navigation: the server redirects to the provider. */}
                      <a href={connectHref(source, `/new?source=${source}`)}>
                        <f.Icon /> Connect {f.name}
                      </a>
                    </Button>
                  ) : (
                    !me.data?.user && (
                      <Button asChild className="mt-2">
                        <Link to="/login">Go to sign in</Link>
                      </Button>
                    )
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        </>
      )}
    </div>
  );
}

function CreateRepo() {
  const me = useMe().data;
  const owners = (me?.orgs ?? []).map((o) => o.name);
  const self = owners.find((o) => o === me?.user?.name.toLowerCase()) ?? owners[0] ?? "";
  const act = useAction();
  const nav = useNavigate();
  const repos = useRepos();
  const [params] = useSearchParams();
  const initial = params.get("source");
  const [source, setSource] = useState<Source>(initial === "empty" ? "empty" : "github");
  const [url, setUrl] = useState("");
  const [branch, setBranch] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [desc, setDesc] = useState("");
  const [init, setInit] = useState(true);
  const asked = params.get("owner");
  const [owner, setOwner] = useState(asked && (owners.includes(asked) || (!me?.user && me?.actor.kind === "admin")) ? asked : self);
  const [priv, setPriv] = useState(true);
  const nameRef = useRef<HTMLInputElement>(null);
  const importing = source !== "empty";
  const bad = name !== "" && !REPO_RE.test(name);
  // Names are unique per owner, as on GitHub: acme/web and globex/web can both exist.
  const taken = !bad && name !== "" && !!repos.data?.some((r) => r.slug === name && r.owner === owner);
  const available = !bad && !taken && name !== "" && repos.isSuccess;
  const badUrl = importing && url !== "" && !URL_RE.test(url.trim());
  const src = SOURCES.find((s) => s.id === source)!;
  const f = forge(source);
  const pick = (r: ProviderRepo) => {
    setUrl(r.url);
    if (!nameTouched) setName(repoNameFromUrl(r.url));
    setBranch(r.default_branch === "main" ? "" : r.default_branch);
  };
  const body = importing
    ? { name, description: desc, owner, private: priv, url: url.trim(), provider: source, branch: branch.trim() || "main", auto_merge: true }
    : { name, description: desc, owner, private: priv, init };
  const step = importing ? { source: 2, name: 3, options: 4 } : { source: 0, name: 2, options: 3 };
  return (
    <div className="mx-auto max-w-[800px]">
      <PageHead title="Create a new repository">
        Agents snapshot, land and verify their work here.
        <span className="mt-0.5 block text-xs italic">Required fields are marked with an asterisk (*).</span>
      </PageHead>
      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          act.mutate({ path: importing ? "/v1/imports" : "/v1/repos", body }, { onSuccess: () => nav(importing ? `${rp(name, owner)}/remote` : rp(name, owner)) });
        }}
      >
        <FormSteps>
          <FormStep n={1} title="How do you want to start?">
            <div role="radiogroup" aria-label="Start from" className="grid grid-cols-3 gap-3 max-md:grid-cols-1">
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={source === s.id}
                  onClick={() => setSource(s.id)}
                  className={cn("flex flex-col items-start gap-1 rounded-md border p-3 text-left hover:bg-transparent-hover", source === s.id && "border-info ring-1 ring-info")}
                >
                  <s.Icon size={20} />
                  <span className="font-semibold">{s.label}</span>
                  <span className="text-xs text-muted-foreground">{s.hint}</span>
                </button>
              ))}
            </div>
          </FormStep>
          {importing && (
            <FormStep n={step.source} title={`Choose the ${f.name} repository`}>
              <SourceAccess key={source} source={source} url={url} onPick={pick}>
                <div className="flex items-start gap-3 max-md:flex-col">
                  <Field
                    label={`${f.name} repository URL`}
                    required
                    invalid={badUrl}
                    className="min-w-0 flex-1 max-md:w-full"
                    hint={badUrl ? "Use an https:// or SSH clone URL, without credentials." : "Paste a clone URL. SSH URLs use bee's key."}
                  >
                    <Input
                      mono
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        if (!nameTouched) setName(repoNameFromUrl(e.target.value));
                      }}
                      placeholder={src.placeholder}
                      aria-invalid={badUrl}
                      required
                    />
                  </Field>
                  <Field label="Branch (optional)" className="w-44 shrink-0 max-md:w-full" hint="Empty uses main.">
                    <Input mono value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
                  </Field>
                </div>
              </SourceAccess>
            </FormStep>
          )}
          <FormStep n={step.name} title="Name it">
            <FieldStack>
              <div className="flex items-start gap-2">
                <Field label="Owner" className="shrink-0">
                  <Select value={owner} onValueChange={setOwner} options={(owner && !owners.includes(owner) ? [owner, ...owners] : owners).map((o) => ({ value: o, label: o }))} aria-label="Owner" />
                </Field>
                <span aria-hidden className="mt-7 text-xl leading-8 text-muted-foreground">
                  /
                </span>
                <Field
                  label="Repository name"
                  required
                  invalid={bad || taken}
                  className="flex-1"
                  hint={
                    bad ? (
                      "Lowercase letters, digits, '.', '_' or '-', starting with a letter or digit."
                    ) : taken ? (
                      <span className="inline-flex items-center gap-1">
                        <XCircleFillIcon size={12} /> {owner ? `${owner}/${name}` : name} already exists.
                      </span>
                    ) : available ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CheckCircleFillIcon size={12} /> {name} is available.
                      </span>
                    ) : importing ? (
                      "Taken from the URL; change it if you like. Cannot be changed later."
                    ) : (
                      "Short and memorable; used in URLs and clone paths and cannot be changed later."
                    )
                  }
                >
                  <Input
                    ref={nameRef}
                    autoFocus={!importing}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setNameTouched(e.target.value !== "");
                    }}
                    placeholder="payments-service"
                    aria-invalid={bad || taken}
                    required
                  />
                </Field>
              </div>
              <Field label="Description" hint={`${desc.length} / ${DESC_MAX} characters`}>
                <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What agents work on here" maxLength={DESC_MAX} />
              </Field>
            </FieldStack>
          </FormStep>
          <FormStep n={step.options} title="Visibility">
            <FieldRows>
              <FieldRow
                title="Private"
                description={priv ? `Only you${owner === self ? "" : ` and ${owner}'s members`} can see this repository.` : "Everyone signed in to this server can read it."}
                control={<Switch aria-label="Private repository" checked={priv} onCheckedChange={setPriv} />}
              />
            </FieldRows>
          </FormStep>
          {!importing && (
            <FormStep n={step.options + 1} title="Options">
              <FieldRows>
                <FieldRow
                  title="Initial commit"
                  description={
                    <>
                      Start main with an empty commit so agents can snapshot and land right away. Turn off to import existing history with <code>bee import</code>.
                    </>
                  }
                  control={<Switch aria-label="Start main with an empty commit" checked={init} onCheckedChange={setInit} />}
                />
              </FieldRows>
            </FormStep>
          )}
        </FormSteps>
        {act.error ? (
          <div className="mb-4">
            <ErrorMessage error={act.error} />
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 border-t border-border-muted pt-4">
          <Button asChild>
            <Link to="/">Cancel</Link>
          </Button>
          <Button variant="primary" disabled={act.isPending || !name || bad || taken || (importing && (!url.trim() || badUrl))}>
            {act.isPending ? (importing ? "Starting import…" : "Creating…") : importing ? "Import repository" : "Create repository"}
          </Button>
        </div>
      </form>
    </div>
  );
}
